// Client for the Worker's JSON API (src/worker/api.ts in the repo root).

export type LaunchStatus =
  | "awaiting_approval"
  | "awaiting_signature"
  | "submitted"
  | "confirmed"
  | "failed"
  | "expired";

export type Launch = {
  launchId: string;
  status: LaunchStatus;
  cluster: "devnet" | "mainnet-beta";
  mint: string;
  name: string;
  symbol: string;
  description: string;
  imageUrl: string | null;
  needsImage: boolean;
  socials: { twitter: string | null; telegram: string | null; website: string | null };
  initialBuySol: string;
  fees: { launchFeeSol: string };
  estimatedTotalSol: string;
  expiresAt: string;
  signature: string | null;
  error: string | null;
  links: { pumpfun: string | null; solscanToken: string; solscanTx: string | null };
  /** The free token site: public once confirmed; previewUrl shows it before. */
  site: { url: string; previewUrl: string } | null;
  /** Share images once the token is live. */
  shareKit: Record<"card" | "square" | "header", string> | null;
};

export type Disclosure = {
  wallet: string;
  totalSol: string;
  totalIsSimulated: boolean;
  firstBuySol: string;
  tokensReceived: string;
  moonlauncherFeeSol: string;
  /** The part of the fee that goes to the partner who referred the creator, paid in the same transaction. */
  referral: { name: string; wallet: string; sol: string } | null;
  networkAndRentSol: string;
  walletBalanceSol: string;
};

export type Built = {
  transaction: string;
  lastValidBlockHeight: number;
  /** wallet-first: sign only, then the server co-signs and sends. server-first: sign and send. */
  signFlow: "wallet-first" | "server-first";
  disclosure: Disclosure;
};

export type SiteConfig = {
  cluster: "devnet" | "mainnet-beta";
  treasury: string | null;
  launchesEnabled: boolean;
  fees: { launchFeeSol: string };
};

/** What a creator wallet can claim (PRD §5.6). */
export type CreatorFees = {
  wallet: string;
  cluster: "devnet" | "mainnet-beta";
  claimable: { bondingCurveSol: string; pumpSwapSol: string; totalSol: string };
  covers: string;
  claimUrl: string | null;
};

export type BuiltClaim = {
  transaction: string;
  lastValidBlockHeight: number;
  disclosure: {
    wallet: string;
    bondingCurveSol: string;
    pumpSwapSol: string;
    totalSol: string;
    networkFeeSol: string;
    unwrapsWsolSol: string | null;
  };
};

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let body: { ok: boolean; data?: T; error?: { code: string; message: string } } | null = null;
  try {
    const res = await fetch(path, init);
    body = await res.json();
  } catch {
    // handled below
  }
  if (!body?.ok) {
    throw new ApiError(
      body?.error?.code ?? "NETWORK",
      body?.error?.message ?? "Couldn't reach Moonlauncher. Check your connection and try again.",
    );
  }
  return body.data as T;
}

const json = (data: unknown): RequestInit => ({
  method: "POST",
  headers: { "content-type": "application/json" },
  body: JSON.stringify(data),
});

/** The /earn portal's dashboard (src/worker/partners.ts partnerDashboard). */
export type PartnerDashboard = {
  code: string;
  name: string;
  wallet: string | null;
  active: boolean;
  sharePercent: number;
  cluster: "devnet" | "mainnet-beta";
  links: { people: string; agent: string; mcp: string };
  statementUrl: string;
  paidSol: string;
  owedSol: string;
  shareSol: string;
  liveLaunches: number;
  inProgress: number;
  launches: {
    name: string;
    symbol: string;
    status: LaunchStatus;
    via: "mcp" | "wallet";
    preparedAt: string;
    shareSol: string | null;
    paid: boolean;
    transaction: string | null;
  }[];
};

export const api = {
  config: () => request<SiteConfig>("/api/config"),
  partnerChallenge: (wallet: string) => request<{ message: string; token: string }>("/api/partners/challenge", json({ wallet })),
  partnerSignIn: (wallet: string, signature: string, token: string) =>
    request<PartnerDashboard>("/api/partners/session", json({ wallet, signature, token })),
  partnerMe: () => request<PartnerDashboard>("/api/partners/me"),
  partnerSignOut: () => request<{ signedOut: boolean }>("/api/partners/logout", json({})),
  launch: (id: string) => request<Launch>(`/api/launches/${encodeURIComponent(id)}`),
  build: (id: string, wallet: string, flow: Built["signFlow"]) =>
    request<Built>(`/api/launches/${encodeURIComponent(id)}/build`, json({ wallet, flow })),
  cosign: (id: string, transaction: string) =>
    request<{ signature: string; launch: Launch }>(`/api/launches/${encodeURIComponent(id)}/cosign`, json({ transaction })),
  sent: (id: string, signature: string) =>
    request<Launch>(`/api/launches/${encodeURIComponent(id)}/sent`, json({ signature })),
  claims: (wallet: string) => request<CreatorFees>(`/api/claims/${encodeURIComponent(wallet)}`),
  buildClaim: (wallet: string) => request<BuiltClaim>(`/api/claims/${encodeURIComponent(wallet)}/build`, json({})),
  claimSent: (wallet: string) => request<{ recorded: boolean }>(`/api/claims/${encodeURIComponent(wallet)}/sent`, json({})),
  claimStatus: (signature: string) =>
    request<{ status: "pending" | "confirmed" | "failed"; solscanTx: string }>(
      `/api/claims/status/${encodeURIComponent(signature)}`,
    ),
  uploadImage: (id: string, file: File) =>
    request<{ imageUrl: string }>(`/api/launches/${encodeURIComponent(id)}/image`, {
      method: "POST",
      headers: { "content-type": file.type },
      body: file,
    }),
};
